<template>
  <base-dialog
    :is-open="isOpen"
    max-width="500"
    @close="close"
  >
    <template #dialog-title>
      {{ label }} beenden
    </template>
    <template #dialog-text>
      <div class="mb-2">Wann soll das Feature enden?</div>
      <v-radio-group
        v-model="mode"
        hide-details
        class="mt-0"
      >
        <v-radio
          label="Jetzt"
          value="now"
        />
        <v-radio
          label="Zum Datum"
          value="date"
        />
      </v-radio-group>
      <v-text-field
        v-if="mode === 'date'"
        v-model="lastDay"
        class="mt-4"
        label="Letzter gültiger Tag"
        type="date"
        :min="today"
        outlined
        dense
        hide-details
      />
      <v-alert
        v-if="feature && feature.inherit && !NON_INHERITABLE_FEATURE_TYPES.includes(feature.type)"
        type="warning"
        dense
        outlined
        class="mt-4 mb-0"
      >
        Das Feature wird vererbt und endet auch für alle untergeordneten Organisationen.
      </v-alert>
      <v-alert
        v-if="error"
        type="error"
        dense
        class="mt-4 mb-0"
      >
        {{ error }}
      </v-alert>
    </template>
    <template #dialog-actions>
      <v-btn
        color="primary"
        :disabled="!isValid || saving"
        :loading="saving"
        @click="save"
      >
        Beenden
      </v-btn>
      <v-btn @click="close">
        Abbrechen
      </v-btn>
    </template>
  </base-dialog>
</template>

<script>
import moment from 'moment'
import BaseDialog from '@/components/UI/BaseDialog.vue'
import { SET_SNACKBAR_MESSAGE } from '@/modules/common/store/mutation-types'
import { formatApiError } from '@/lib/api-errors'
import { UPDATE_TIME } from '@/modules/common/store/action-types'
import { lastValidDay, validNotafterFromLastDay } from '@/lib/sort-utils'
import { NON_INHERITABLE_FEATURE_TYPES } from '@/lib/settings-tools'

export default {
  name: 'FeatureEndDialog',
  components: { BaseDialog },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    feature: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'ended'],
  data () {
    return {
      NON_INHERITABLE_FEATURE_TYPES,
      mode: 'now',
      lastDay: '',
      saving: false,
      error: null
    }
  },
  computed: {
    today () {
      return moment().format('YYYY-MM-DD')
    },
    isValid () {
      return this.mode === 'now' || (!!this.lastDay && this.lastDay >= this.today)
    }
  },
  watch: {
    isOpen (val) {
      if (!val) return
      this.error = null
      this.saving = false
      this.mode = 'now'
      // Prefill an already planned end, shown as the last valid day
      this.lastDay = lastValidDay(this.feature?.valid_notafter)?.format('YYYY-MM-DD') ?? ''
    }
  },
  methods: {
    async save () {
      this.saving = true
      this.error = null
      // valid_notafter is exclusive: ending "at" a date means the next day's start
      const validNotafter = this.mode === 'now'
        ? moment().format()
        : validNotafterFromLastDay(this.lastDay).format()
      try {
        await this.$store.getters.restApi.patch(`feature/${this.feature.id}/`, { valid_notafter: validNotafter })
        // The store's clock only ticks every minute; refresh it so "Jetzt" shows as ended right away
        await this.$store.dispatch(UPDATE_TIME)
        this.$store.commit(SET_SNACKBAR_MESSAGE, {
          message: `${this.label} wurde beendet.`,
          color: 'success'
        })
        this.$emit('ended')
        this.close()
      } catch (e) {
        this.error = formatApiError(e)
      } finally {
        this.saving = false
      }
    },
    close () {
      this.$emit('close')
    }
  }
}
</script>
